import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Bell, Search, LogOut, ChevronRight, Building2, KeyRound, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { breadcrumbFor } from "./Sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function ProfileDialog({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(user?.name || ""); }, [open, user]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/auth/profile", { name }); await refreshUser(); toast.success("Nome aggiornato"); onOpenChange(false); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-dialog">
        <DialogHeader><DialogTitle>Il mio profilo</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e cognome" required minLength={2} data-testid="profile-name-input" />
          <p className="text-xs text-slate-500">Email: {user?.email} · Ruolo: {user?.role_label}. Email e ruolo si cambiano da Amministrazione → Utenti.</p>
          <Button type="submit" disabled={busy} className="w-full" data-testid="profile-submit">Salva</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ open, onOpenChange }) {
  const [oldP, setOld] = useState(""); const [newP, setNew] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/change-password", { old_password: oldP, new_password: newP }); toast.success("Password aggiornata"); onOpenChange(false); setOld(""); setNew(""); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Cambia password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="password" placeholder="Password attuale" value={oldP} onChange={(e) => setOld(e.target.value)} required data-testid="old-password-input" />
          <Input type="password" placeholder="Nuova password (min 6)" value={newP} onChange={(e) => setNew(e.target.value)} required minLength={6} data-testid="new-password-input" />
          <Button type="submit" disabled={busy} className="w-full" data-testid="change-password-submit">Salva</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Header({ onOpenSearch }) {
  const { user, sites, siteId, setSiteId, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notif, setNotif] = useState({ alerts: [], notifications: [], count: 0 });
  const [pwOpen, setPwOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const crumbs = breadcrumbFor(location.pathname);

  useEffect(() => {
    if (!siteId) return;
    let alive = true;
    const load = () => api.get("/notifications").then((r) => alive && setNotif(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [siteId, location.pathname]);

  const markAll = async () => { await api.post("/notifications/read-all"); setNotif((n) => ({ ...n, notifications: [], count: n.alerts.length })); };

  return (
    <header className="no-print sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 md:px-6" data-testid="app-header">
      <nav className="hidden items-center gap-1 text-sm text-slate-500 md:flex" aria-label="Percorso" data-testid="breadcrumb">
        {crumbs.map((c, i) => (
          <span key={c} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5" />}
            <span className={i === crumbs.length - 1 ? "font-semibold text-slate-900" : ""}>{c}</span>
          </span>
        ))}
      </nav>
      <div className="flex min-w-0 items-center gap-2 md:hidden"><span className="truncate text-base font-bold text-slate-900">{crumbs[crumbs.length - 1]}</span></div>
      <div className="ml-auto flex items-center gap-2">
        <Select value={siteId} onValueChange={(v) => { setSiteId(v); navigate(location.pathname); }}>
          <SelectTrigger className="h-9 w-[150px] bg-white text-sm md:w-[240px]" data-testid="site-selector" aria-label="Seleziona sede">
            <Building2 className="mr-1.5 h-4 w-4 shrink-0 text-slate-500" /><SelectValue placeholder="Sede" />
          </SelectTrigger>
          <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`site-option-${s.id}`}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={onOpenSearch} aria-label="Ricerca universale (Ctrl+K)" data-testid="search-button"><Search className="h-4 w-4" /></Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="relative h-9 w-9" aria-label={`Notifiche: ${notif.count}`} data-testid="notifications-button">
              <Bell className="h-4 w-4" />
              {notif.count > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#A13544] px-1 text-[10px] font-bold text-white tnum" data-testid="notifications-count">{notif.count}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[360px] p-0" data-testid="notifications-panel">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-semibold">Notifiche</span>
              {notif.notifications.length > 0 && <button className="text-xs text-primary hover:underline" onClick={markAll} data-testid="mark-all-read">Segna tutte lette</button>}
            </div>
            <ul className="max-h-[360px] divide-y overflow-y-auto">
              {notif.count === 0 && <li className="px-3 py-6 text-center text-sm text-slate-500">Nessun avviso: tutto sotto controllo</li>}
              {notif.notifications.map((n) => (
                <li key={n.id} className="px-3 py-2.5 text-sm">
                  <Link to={n.link || "#"} className="hover:underline" onClick={() => api.post(`/notifications/${n.id}/read`)}>{n.message}</Link>
                </li>
              ))}
              {notif.alerts.map((a) => (
                <li key={`${a.type}-${a.message}`} className="flex items-start gap-2 px-3 py-2.5 text-sm">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.severity === "danger" ? "bg-[#A13544]" : "bg-[#D97706]"}`} />
                  <Link to={a.link || "#"} className="hover:underline">{a.message}</Link>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex h-9 items-center gap-2 rounded-md border border-slate-200 px-2 text-sm hover:bg-slate-50" data-testid="user-menu-button">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1F5F57] text-xs font-bold text-white">{user?.name?.[0]}</span>
              <span className="hidden max-w-[140px] truncate md:inline">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel><div className="text-sm font-semibold">{user?.name}</div><div className="text-xs font-normal text-slate-500">{user?.role_label} · {user?.email}</div></DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="menu-profile"><UserRound className="h-4 w-4" /> Il mio profilo (nome)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="menu-change-password"><KeyRound className="h-4 w-4" /> Cambia password</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4" /> Esci</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <PasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </header>
  );
}
